import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const api = axios.create({
    baseURL: process.env.NEXT_PUBLIC_BASE_API_URL,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true, // ✅ This sends cookies automatically
});

// Refresh token management
let isRefreshing = false;
let failedQueue: Array<{
    resolve: () => void;
    reject: (error: any) => void;
}> = [];

const processQueue = (error: any = null) => {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve();
        }
    });
    
    failedQueue = [];
};

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle non-401 errors immediately
        if (error.response?.status !== 401) {
            return Promise.reject(error);
        }

        // ⚠️ CRITICAL: Don't retry or redirect for these endpoints
        const url = originalRequest.url || '';
        const skipInterceptorPaths = [
            '/auth/login',
            '/auth/register',
            '/auth/refresh',
            '/auth/logout',
            '/auth/me',          // ✅ NEW: Skip /me endpoint (used for checking auth)
            '/auth/verify'       // ✅ NEW: Skip verify endpoint
        ];

        if (skipInterceptorPaths.some(path => url.includes(path))) {
            console.log('🚫 Skipping interceptor for auth endpoint:', url);
            return Promise.reject(error);
        }

        // Don't retry if already retried
        if (originalRequest._retry) {
            console.log('🚫 Request already retried, rejecting');
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // If already refreshing, queue this request
        if (isRefreshing) {
            console.log('⏳ Queueing request while refresh in progress');
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then(() => {
                    // ✅ No need to update Authorization header
                    // Cookies are sent automatically
                    return api(originalRequest);
                })
                .catch((err) => Promise.reject(err));
        }

        // Start refresh process
        console.log('🔄 Starting token refresh from interceptor...');
        isRefreshing = true;

        try {
            // ✅ Refresh endpoint sets new access_token cookie automatically
            const { data } = await axios.post(
                `${process.env.NEXT_PUBLIC_BASE_API_URL}/api/auth/refresh`,
                {},
                { withCredentials: true }
            );

            console.log('✅ Token refreshed successfully via interceptor');
            console.log('   New cookie set by backend');

            // ✅ Process all queued requests (no token to pass)
            processQueue(null);

            // ✅ Retry original request with new cookie
            return api(originalRequest);

        } catch (refreshError: any) {
            console.error('❌ Refresh failed in interceptor:', refreshError.response?.status);
            
            // Process queue with error
            processQueue(refreshError);

            // Handle rate limiting specifically
            if (refreshError.response?.status === 429) {
                console.error('⚠️ Rate limit exceeded on token refresh');
                
                if (typeof window !== 'undefined') {
                    alert('Too many refresh attempts. Please login again.');
                }
            }

            // ✅ FIXED: Only redirect to login from PROTECTED pages, not public pages
            if (refreshError.response?.status === 401 && typeof window !== 'undefined') {
                const currentPath = window.location.pathname;
                
                // Define protected paths that require authentication
                const protectedPaths = [
                    '/myaccount',
                    '/account',
                    '/profile',
                    '/orders',
                    '/dashboard',
                    '/settings',
                    '/inbox',
                ];
                
                // Check if current page is protected
                const isProtectedPath = protectedPaths.some(path => 
                    currentPath.startsWith(path)
                );
                
                if (isProtectedPath) {
                    console.log('🔓 Redirecting to login from protected page...');
                    setTimeout(() => {
                        window.location.href = `/login?redirect=${currentPath}`;
                    }, 100);
                } else {
                    console.log('ℹ️ 401 on public page - ignoring (normal for guests)');
                }
            }

            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

// =============================================================================
// ANALYTICS UTILITIES
// Fire-and-forget tracking methods that won't impact UX
// =============================================================================

/**
 * Track analytics event (fire-and-forget)
 * Silently fails without throwing errors
 * 
 * @param endpoint - API endpoint (e.g., '/api/analytics/track')
 * @param data - Event data to track
 */
export async function trackEvent(
    endpoint: string,
    data: Record<string, any>
): Promise<void> {
    try {
        // Use a separate axios instance without interceptors for analytics
        // This prevents analytics from getting caught in auth retry logic
        await axios.post(endpoint, data, {
            baseURL: process.env.NEXT_PUBLIC_BASE_API_URL,
            headers: { "Content-Type": "application/json" },
            withCredentials: true, // ✅ Send cookies for session tracking
            timeout: 5000, // Don't wait too long
        });
    } catch (error) {
        // Silently fail - don't impact UX
        console.debug('Analytics tracking failed:', error);
    }
}

/**
 * Track multiple events in a batch
 * 
 * @param endpoint - Batch endpoint
 * @param events - Array of event data
 */
export async function trackEventBatch(
    endpoint: string,
    events: Array<Record<string, any>>
): Promise<void> {
    try {
        await axios.post(endpoint, { events }, {
            baseURL: process.env.NEXT_PUBLIC_BASE_API_URL,
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
            timeout: 5000,
        });
    } catch (error) {
        console.debug('Batch analytics tracking failed:', error);
    }
}

export default api;